import { Plus, Check } from "lucide-react";
import { media } from "@/components/public/Blocks";

export function Head({ eyebrow, title, text, center }) {
  return (
    <div className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      {eyebrow && <p className="p-eyebrow">{eyebrow}</p>}
      <h2 className="mt-2 font-display text-4xl uppercase leading-[0.95] sm:text-5xl">{title}</h2>
      {text && <p className="mt-3 text-base p-t2">{text}</p>}
    </div>
  );
}

export function Apercu({ block, fallback, testId }) {
  const img = media(block?.media_url);
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 md:grid-cols-[1fr_1.1fr]" data-testid={testId}>
      <div>
        <Head eyebrow={block?.eyebrow || "Aperçu"} title={block?.title || "À quoi ça ressemble"} text={block?.subtitle} />
        <p className="mt-4 flex items-center gap-2 text-xs p-t3"><span className="h-2 w-2 rounded-full bg-[#B7F52A]" /> {img ? "Visuel de démonstration" : "Aperçu illustratif, données fictives"}</p>
      </div>
      <div className="flex justify-center" data-testid={`${testId}-visual`}>
        {img ? <img src={img} alt={block?.title || "Aperçu"} className="max-h-[640px] w-full rounded-sm object-contain" /> : fallback}
      </div>
    </section>
  );
}

export function IconGrid({ items, cols = "lg:grid-cols-3", testId }) {
  return (
    <div className={`mt-8 grid gap-3 sm:grid-cols-2 ${cols}`} data-testid={testId}>
      {items.map(([I, t, d], i) => (
        <div key={t} className="p-surface group p-6 transition-transform duration-300 hover:-translate-y-1" data-testid={`${testId}-${i}`}>
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#B7F52A] text-[#080C0D] transition-transform group-hover:scale-110"><I size={20} /></span>
          <p className="mt-4 text-lg font-bold">{t}</p>
          <p className="mt-1 text-sm p-t2">{d}</p>
        </div>))}
    </div>
  );
}

export function NumberedSteps({ items, testId }) {
  return (
    <ol className="mt-8 grid gap-3 md:grid-cols-3" data-testid={testId}>
      {items.map(([I, t, d], i) => (
        <li key={t} className="p-surface relative overflow-hidden p-6" data-testid={`${testId}-${i + 1}`}>
          <span className="absolute -right-1 -top-5 font-display text-8xl opacity-[0.07]">{i + 1}</span>
          <span className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-[#B7F52A] text-sm font-bold text-[#080C0D]">{i + 1}</span><I size={20} /></span>
          <h3 className="mt-4 text-lg font-bold">{t}</h3>
          <p className="mt-1 text-sm p-t2">{d}</p>
        </li>))}
    </ol>
  );
}

export function ForWho({ title, items, custom, testId }) {
  return (
    <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-2" data-testid={testId}>
      <div>
        <Head eyebrow="Pour qui ?" title={title} />
        <ul className="mt-8 space-y-5">{items.map(([I, t, d]) => (
          <li key={t} className="flex gap-4"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border p-line"><I size={19} /></span>
            <span><span className="block font-bold">{t}</span><span className="text-sm p-t2">{d}</span></span></li>))}</ul>
      </div>
      <div className="p-surface p-6 md:p-8" data-testid={`${testId}-custom`}>
        <p className="font-display text-3xl uppercase">{custom.title}</p>
        <p className="mt-1 text-sm p-t2">{custom.text}</p>
        <ul className="mt-6 grid gap-3">{custom.items.map((c) => (
          <li key={c} className="flex items-start gap-3 text-sm"><span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#B7F52A] text-[#080C0D]"><Check size={12} strokeWidth={3} /></span> {c}</li>))}</ul>
      </div>
    </section>
  );
}

export function StaticFaq({ items, testId = "pub-faq" }) {
  return (
    <section className="mx-auto max-w-4xl px-5 py-16" data-testid={testId}>
      <Head eyebrow="Questions fréquentes" title="Tout ce qu'on nous demande" />
      <div className="mt-6 border-t p-line">{items.map(([q, a], i) => (
        <details key={q} className="group border-b p-line py-4" data-testid={`${testId}-${i + 1}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-lg font-bold">{q}<Plus size={18} className="shrink-0 transition-transform group-open:rotate-45" /></summary>
          <p className="mt-2 text-base p-t2">{a}</p>
        </details>))}</div>
    </section>
  );
}
